import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { adminFetch } from '../../lib/adminFetch';
import { API_BASE } from '../../lib/apiBase';
import { Card, CardContent } from '../../components/ui/card';
import {
  Calendar,
  Inbox,
  MessageCircle,
  CheckCircle2,
  CalendarCheck2,
  Mail,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = API_BASE;

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminFetch(`${BACKEND_URL}/api/admin/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    return () => clearInterval(id);
  }, [fetchStats]);

  // Primary operational cards
  const primaryCards = [
    {
      title: 'New Requests',
      value: stats?.new_requests ?? 0,
      subtitle: 'Awaiting first contact',
      icon: Inbox,
      ring: 'ring-red-100',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      accent: 'text-red-700',
      link: '/admin/appointments?status=new_request',
      testid: 'card-new-requests',
    },
    {
      title: 'In Progress',
      value: stats?.in_progress ?? 0,
      subtitle: 'Contacted · awaiting reply · rescheduled',
      icon: MessageCircle,
      ring: 'ring-amber-100',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      accent: 'text-amber-700',
      link: '/admin/appointments',
      testid: 'card-in-progress',
    },
    {
      title: 'Confirmed Today',
      value: stats?.confirmed_today ?? 0,
      subtitle: "Patients scheduled for today",
      icon: CalendarCheck2,
      ring: 'ring-emerald-100',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      accent: 'text-emerald-700',
      link: '/admin/appointments?status=confirmed',
      testid: 'card-confirmed-today',
    },
    {
      title: 'Completed This Week',
      value: stats?.completed_this_week ?? 0,
      subtitle: 'Last 7 days',
      icon: CheckCircle2,
      ring: 'ring-blue-100',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      accent: 'text-blue-700',
      link: '/admin/appointments?status=completed',
      testid: 'card-completed-week',
    },
  ];

  // Secondary supplementary
  const secondaryCards = [
    {
      title: 'Total Appointments',
      value: stats?.total_appointments ?? 0,
      subtitle: 'All-time',
      icon: Calendar,
      link: '/admin/appointments',
    },
    {
      title: 'Total Patients',
      value: stats?.total_patients ?? 0,
      subtitle: 'In EMR',
      icon: Users,
      link: '/admin/patients',
    },
    {
      title: 'Unread Messages',
      value: stats?.unread_messages ?? 0,
      subtitle: 'Contact form',
      icon: Mail,
      link: '/admin/messages',
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 font-[Montserrat]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#14D1A2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl font-[Montserrat]">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-sm text-gray-500 mb-8">Live operational view — refreshes every 30 seconds.</p>

        {/* Primary live cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {primaryCards.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.title} to={s.link} data-testid={s.testid}>
                <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ring-1 ${s.ring}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-10 h-10 ${s.iconBg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${s.iconColor}`} />
                      </div>
                    </div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{s.title}</p>
                    <p className={`text-4xl font-bold ${s.accent}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 mt-2">{s.subtitle}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Secondary cards */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {secondaryCards.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.title} to={s.link}>
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">{s.title}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
                        <p className="text-xs text-gray-400 mt-1">{s.subtitle}</p>
                      </div>
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Quick actions */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Link to="/admin/appointments?status=new_request">
                <div className="p-4 bg-red-50 hover:bg-red-100 transition-colors rounded-lg border border-red-100">
                  <p className="font-semibold text-red-700">Action: New Requests</p>
                  <p className="text-sm text-red-600/80 mt-1">Contact patients waiting for first follow-up</p>
                </div>
              </Link>
              <Link to="/admin/appointments?status=confirmed">
                <div className="p-4 bg-emerald-50 hover:bg-emerald-100 transition-colors rounded-lg border border-emerald-100">
                  <p className="font-semibold text-emerald-700">Today&apos;s Confirmed</p>
                  <p className="text-sm text-emerald-600/80 mt-1">Review schedule for the day</p>
                </div>
              </Link>
              <Link to="/admin/messages">
                <div className="p-4 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-700">Contact Messages</p>
                  <p className="text-sm text-gray-500 mt-1">Respond to website enquiries</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
